import React, { useEffect, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../services/api';

type Contact = { name?: string; email?: string; phone?: string };
type SyncReceipt = { id: string; status: string; detail?: string };
export type ContactInvoice = {
  id: string;
  status: string;
  customer_details: Contact;
  metadata?: Record<string, any>;
};
type Preview = {
  invoice_id: string;
  kind: 'draft_refresh' | 'delivery_override';
  expected_revision: string;
  fingerprint: string;
  before: Contact;
  after: Contact;
  reason: string;
  local_customer_id?: string;
};

export default function InvoiceContactEditor({ invoice, onSaved }: {
  invoice: ContactInvoice;
  onSaved: (invoice: ContactInvoice) => void;
}) {
  const [current, setCurrent] = useState(invoice);
  const [reason, setReason] = useState('');
  const [recipient, setRecipient] = useState(invoice.metadata?.delivery_recipient_override?.email || invoice.customer_details.email || '');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [unknown, setUnknown] = useState(false);
  useEffect(() => {
    setCurrent(invoice);
    setRecipient(invoice.metadata?.delivery_recipient_override?.email || invoice.customer_details.email || '');
    setPreview(null);
    setReviewed(false);
  }, [invoice]);
  const draft = current.status === 'draft';
  const editable = draft || ['open', 'paid'].includes(current.status);
  const sync: SyncReceipt | undefined = current.metadata?.contact_sync;
  const payload = draft
    ? { kind: 'draft_refresh', local_customer_id: current.metadata?.local_customer_id, reason }
    : { kind: 'delivery_override', recipient, reason };

  function saved(updated: ContactInvoice) {
    setCurrent(updated);
    onSaved(updated);
    setUnknown(false);
    setPreview(null);
    setReviewed(false);
    setMessage('Local correction saved.');
  }

  async function readReceipt(review: Preview) {
    try {
      const { data } = await apiClient.get(`/invoices/${current.id}`);
      const updated: ContactInvoice = data.invoice || data;
      const committed = updated.metadata?.contact_change_receipts?.some((entry: any) => entry.fingerprint === review.fingerprint);
      if (committed) saved(updated);
      else setMessage('No matching receipt found. Outcome remains uncertain; no correction was retried.');
    } catch {
      setMessage('Receipt readback unavailable. Outcome remains uncertain.');
    }
  }

  async function loadPreview() {
    setBusy(true);
    setMessage('');
    try {
      const { data } = await apiClient.post(`/invoices/admin/${current.id}/contact-preview`, payload);
      setPreview(data);
      setReviewed(false);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Contact preview unavailable.');
    } finally { setBusy(false); }
  }

  async function apply() {
    if (!preview || !reviewed || unknown) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/invoices/admin/${current.id}/contact-change`, {
        ...payload, expected_revision: preview.expected_revision, fingerprint: preview.fingerprint,
      }, { timeout: 30000 });
      saved(data.invoice);
    } catch (error: any) {
      if (error?.response?.status && error.response.status < 500) {
        setPreview(null);
        setReviewed(false);
        setMessage(error.response.data?.message || 'Correction rejected; review again.');
      } else {
        setUnknown(true);
        setMessage('Correction outcome is uncertain. Checking the original invoice receipt.');
        await readReceipt(preview);
      }
    } finally { setBusy(false); }
  }

  async function reconcile() {
    if (!sync) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/invoices/admin/${current.id}/contact-sync/${sync.id}/reconcile`);
      const updated = { ...current, metadata: { ...current.metadata, contact_sync: data.provider_sync } };
      setCurrent(updated);
      onSaved(updated);
      setMessage(data.provider_sync.detail || 'Stripe contact checked.');
    } catch { setMessage('Stripe readback unavailable. Local correction remains saved.'); }
    finally { setBusy(false); }
  }

  function invalidateReview() { setPreview(null); setReviewed(false); setMessage(''); }

  return <section className="space-y-3 border-t border-ghost py-4" aria-label="Invoice contacts">
    <h4 className="text-sm font-semibold text-on-surface">{draft ? 'Draft Bill-To Contact' : 'Historical Bill-To'}</h4>
    <div className="text-sm break-words">
      <p>{current.customer_details.name}</p><p>{current.customer_details.email}</p><p>{current.customer_details.phone}</p>
    </div>
    {!draft && <div className="text-sm break-words"><span className="font-medium">Delivery Recipient: </span>
      {current.metadata?.delivery_recipient_override?.email || current.customer_details.email}</div>}
    {editable && <div className="space-y-3">
      {!draft && <label className="block text-sm">Delivery Email
        <input className="input-field mt-1 w-full" type="email" value={recipient} disabled={busy || unknown}
          onChange={(event) => { setRecipient(event.target.value); invalidateReview(); }} />
      </label>}
      <label className="block text-sm">Correction Reason
        <textarea className="input-field mt-1 w-full" maxLength={1000} value={reason} disabled={busy || unknown}
          onChange={(event) => { setReason(event.target.value); invalidateReview(); }} />
      </label>
      {!unknown && <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={loadPreview}
        disabled={busy || reason.trim().length < 5 || (draft && !current.metadata?.local_customer_id)}>
        <MagnifyingGlassIcon className="h-4 w-4" />Review Correction
      </button>}
    </div>}
    {preview && <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div className="min-w-0 break-words"><dt className="font-medium">Before</dt><dd>{preview.before.name}</dd><dd>{preview.before.email}</dd><dd>{preview.before.phone}</dd></div>
        <div className="min-w-0 break-words"><dt className="font-medium">After</dt><dd>{preview.after.name}</dd><dd>{preview.after.email}</dd><dd>{preview.after.phone}</dd></div>
      </dl>
      {!unknown && <><label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={reviewed} disabled={busy}
        onChange={(event) => setReviewed(event.target.checked)} />Reviewed Contact Correction</label>
        <button type="button" className="btn-primary inline-flex items-center gap-2" disabled={!reviewed || busy} onClick={apply}>
          <CheckIcon className="h-4 w-4" />Save Correction
        </button></>}
      {unknown && <button className="btn-secondary inline-flex items-center gap-2" type="button" disabled={busy}
        onClick={async () => { setBusy(true); await readReceipt(preview); setBusy(false); }}>
        <ArrowPathIcon className="h-4 w-4" />Check Receipt
      </button>}
    </div>}
    {sync && <div className="space-y-2 text-sm"><p>Stripe Contact: {sync.status.replace(/_/g, ' ')}</p>
      {sync.detail && <p className="break-words">{sync.detail}</p>}
      {sync.status !== 'not_linked' && <button type="button" className="btn-secondary inline-flex items-center gap-2" disabled={busy} onClick={reconcile}>
        <ArrowPathIcon className="h-4 w-4" />Check Stripe Contact
      </button>}
    </div>}
    {message && <p role="status" className="text-sm break-words">{message}</p>}
  </section>;
}
